<script lang="ts" setup>
// n > 0 の時のみしか想定していないので、0件の時はこれ使わないこと
// TODO i18n対応
import { IPagingOptions } from '@/coreDomainsModule/paging';
import { computed, ref, watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { onMounted } from 'vue';
import { i18n } from '@/i18n';

const NORMAL_PAGE_COUNT_OPTIONS = [
  { text: '5', value: 5 },
  { text: '30', value: 30 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
] as const;

const ORDER_PAGE_COUNT_OPTIONS = [
  { text: '5', value: 5 },
  { text: '30', value: 30 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
  { text: '300', value: 300 },
  { text: '500', value: 500 },
] as const;

// NOTE: 在庫管理は表示可能なLimitを増やす予定がありますが、現状BEのリクエストが死ぬので他と同じにしています
const INVENTORY_PAGE_COUNT_OPTIONS = [
  { text: '5', value: 5 },
  { text: '30', value: 30 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
] as const;

const t = i18n.t;

// props
const props = defineProps<{
  pagingOptions: IPagingOptions;
  isLoading: boolean;
  isInventoryPage?: boolean; // 現状在庫管理ページのみ100件以上の表示に対応しているため
  isOrderPage?: boolean;
  setPageNumber?: number; // 現在のページ番号を明示的に変更する場合に使用
  noBottomMargin?: boolean;
}>();

// emit
const emits = defineEmits<{
  (e: 'onChangePage', pagingOptions: IPagingOptions): void;
}>();

const pageNumberOptions = computed(() => {
  return Array.from({ length: Math.ceil(pageOptions.value.totalResultsAvailable / pageOptions.value.pageSize) }, (_, i) => i + 1).map(
    (i) => ({
      text: i.toString(),
      value: i,
    })
  );
});

const pageCountOptions = computed(() => {
  if (props.isOrderPage) {
    return ORDER_PAGE_COUNT_OPTIONS;
  }
  if (props.isInventoryPage) {
    return INVENTORY_PAGE_COUNT_OPTIONS;
  }
  return NORMAL_PAGE_COUNT_OPTIONS;
});

/**
 * 初期表示のページネーションのページ数を設定
 * @param initValue - 初期表示のページネーションのページ数
 */
const initPageSize = (initValue: number): number => {
  if (initValue > 1) {
    return initValue;
  } else {
    return 30;
  }
};

const pageOptions = ref({
  offset: props.pagingOptions.offset,
  pageSize: initPageSize(props.pagingOptions.pageSize),
  totalResultsAvailable: props.pagingOptions.totalResultsAvailable ?? 1, // totalResultsAvailableがない場合はありえない(型使いまわしているせいでこうしてるだけ)
  returnCount: props.pagingOptions.returnCount,
});

const currentPage = ref(1);

/**
 * ページネーションのページ数を選択したときの処理
 * @param event - イベント(number型想定)
 */
const onSelectedPageCount = (event) => {
  currentPage.value = Number(event.target.value);
  pageOptions.value.offset = pageOptions.value.pageSize * (currentPage.value - 1);
  emits('onChangePage', pageOptions.value);
};

/**
 * ページネーションの表示件数を選択したときの処理
 * @param event - イベント(number型想定)
 */
const onSelectedPageDisplayCount = (event) => {
  pageOptions.value.pageSize = Number(event.target.value);
  currentPage.value = 1; // ページを1に戻す
  pageOptions.value.offset = 0; // offsetを0に戻す
  emits('onChangePage', pageOptions.value);
};

/**
 * ページネーションをクリックしたときの処理
 * @param isPrev - 前のページへ移動するかどうか
 */
const onChangePage = (isPrev: boolean) => {
  if (isPrev) {
    currentPage.value -= 1;
  } else {
    currentPage.value += 1;
  }
  pageOptions.value.offset = pageOptions.value.pageSize * (currentPage.value - 1);

  emits('onChangePage', pageOptions.value);
};

watch(
  () => props.pagingOptions,
  (newVal) => {
    if (newVal.offset === 0) {
      // offsetが0にリセットされた場合はページを1に戻す
      currentPage.value = 1;
    }
    pageOptions.value = {
      offset: newVal.offset,
      pageSize: initPageSize(newVal.pageSize),
      totalResultsAvailable: newVal.totalResultsAvailable ?? 1, // totalResultsAvailableがない場合はありえない(型使いまわしているせいでこうしてるだけ)
      returnCount: newVal.returnCount,
    };
  },
  { deep: true }
);

const pageCounterText = computed(() => {
  const start = pageOptions.value.pageSize * (currentPage.value - 1) + 1;
  const end = Math.min(start + pageOptions.value.pageSize - 1, pageOptions.value.totalResultsAvailable);
  return t('ページネーション表示件数', {
    start: start,
    end: end,
    total: pageOptions.value.totalResultsAvailable,
  });
});

const isDisabledPrev = computed(() => currentPage.value === 1);
const isDisabledNext = computed(() => currentPage.value >= pageNumberOptions.value.length);

watch(
  () => props.setPageNumber,
  (next) => {
    if (Number.isSafeInteger(next)) {
      currentPage.value = next as number;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="pagination-container" :class="props.isLoading ? 'pagination-container--loading' : ''">
    <div class="pagination-container__page-info page-info">
      <select :value="currentPage" class="page-info__select-box" @input="onSelectedPageCount($event)">
        <option v-for="(option, index) in pageNumberOptions" :key="index" :value="option.value">
          {{ option.text }}
        </option>
      </select>
      <label for="display-count" class="page-info__label">{{ t('ページ目を表示') }}</label>
    </div>

    <div class="pagination-container__page-counter page-counter">
      <span class="page-counter__text"> {{ pageCounterText }} </span>
    </div>

    <div class="pagination-container__display-count display-count">
      <label for="display-count" class="display-count__label">{{ t('表示件数(前)') }}</label>

      <select :value="pageOptions.pageSize" class="display-count__select-box" @input="onSelectedPageDisplayCount($event)">
        <option v-for="(option, index) in pageCountOptions" :key="index" :value="option.value">
          {{ option.text }}
        </option>
      </select>

      <label for="display-count" class="display-count__label">{{ t('表示件数(後)') }}</label>
    </div>

    <div class="pagination-container__page-move page-move">
      <SvgLoader :disabled="isDisabledPrev" name="arrowLeft" class="page-move__left" @click="onChangePage(true)" />
      <SvgLoader :disabled="isDisabledNext" name="arrowRight" class="page-move__right" @click="onChangePage(false)" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.pagination-container {
  /* height: 100px; // レイアウト仮組用 */
  display: grid;
  align-items: center;
  grid-column-gap: 36px;
  grid-template-columns: 1fr repeat(3, fit-content(100%));
  margin-right: 12px;
  margin-bottom: v-bind('props.noBottomMargin ? "0px" : "16px"');

  &--loading {
    pointer-events: none;
  }

  &__page-info {
    justify-self: end;
  }
}

.page-info {
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 8px;

  &__label {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: $textDefault;
  }
}

.page-counter {
  &__text {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: $textDefault;
  }
}

.display-count {
  display: grid;
  grid-template-columns: auto auto auto;

  column-gap: 8px;
  &__label {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: $textDefault;
  }
}

.page-move {
  display: grid;
  grid-template-columns: repeat(2, auto);
  align-items: center;
  grid-column-gap: 36px;

  &__left {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }

  &__right {
    cursor: pointer;

    width: 18px;
    height: 18px;
  }
}
</style>
